/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import { FormControlLabel, Grid, makeStyles, Switch } from '@material-ui/core';
import Button from 'components/Button';
import ExplanationHover from 'components/ExplanationHover';
import { Formik } from 'formik';
import i18n from 'i18n';
import { RolesEnum } from 'modules/authenticate/interfaces';
import { ClientFields, ClientUsers } from 'modules/clients/interfaces';
import React from 'react';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { applyI18n } from 'utils/helpers';
import { isClientOperator } from 'utils/role';
import formStyles from '../styles';
import screenFields from './formFields';
import { initialValues } from './initialValues';
import validationSchema from './schema';
import AuditSession from './AuditSession';

const useStyles = makeStyles(formStyles);

interface SwitchComponentProps {
  id?: string;
  label: string;
  isChecked?: boolean;
  children?: React.ReactNode;
  hidden?: boolean;
  explanationHover?: React.ReactNode;
  onChange: (e: any, value: any) => void;
}

const SwitchComponent = ({
  id,
  label,
  isChecked,
  children,
  hidden,
  explanationHover,
  onChange,
}: SwitchComponentProps) => {
  const classes = useStyles();

  return hidden ? null : (
    <Grid item xs={6}>
      <Grid item className={classes.switchContainer} xs={12}>
        <FormControlLabel
          classes={{ label: classes.switchLabel }}
          control={
            <Switch
              id={id}
              checked={isChecked}
              onChange={onChange}
              classes={{ colorSecondary: classes.switchChecked }}
              size="small"
            />
          }
          label={label}
        />
        {!!explanationHover && explanationHover}
      </Grid>
      {!!children && isChecked && children}
    </Grid>
  );
};

interface Props {
  client: ClientFields | null;
  isEdit?: boolean;
  defaultValues?: ClientUsers;
  role?: RolesEnum;
  onSubmit: (values: typeof initialValues) => void;
  onCancel: () => void;
}

const FormUser = ({
  client,
  isEdit,
  defaultValues,
  role,
  onSubmit,
  onCancel,
}: Props) => {
  const classes = useStyles();
  let saltToggle = defaultValues?.ad_password_salt?.skip;

  return (
    <Grid item container className={classes.formUser}>
      <Formik
        initialValues={defaultValues || initialValues}
        validationSchema={validationSchema(role, isEdit)}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ setFieldValue, submitForm, errors, values }) => (
          <Grid container spacing={2}>
            <GenericForm
              fields={values}
              screenFields={applyI18n(
                screenFields.slice(
                  0,
                  client?.active_directory?.partner_ldap_type === 'externo'
                    ? 4
                    : 3
                ),
                i18n.language
              ).map(item => {
                return {
                  ...item,
                  disabled:
                    item?.stateKey === 'email' && !!defaultValues?.email,
                };
              })}
              setFieldValue={setFieldValue}
              errors={errors}
            />
            {client?.active_directory?.remote_profile && (
              <>
                <SwitchComponent
                  id="toggle-remote-profile"
                  label={i18n.t('REMOTE_PROFILE_KEY')}
                  onChange={(e, value) => setFieldValue('use_profile', value)}
                  isChecked={values?.use_profile}
                />
                <GenericForm
                  fields={{
                    ...values,
                    profile_path: client?.active_directory?.profile_path,
                  }}
                  screenFields={applyI18n(
                    screenFields.slice(4, 5),
                    i18n.language
                  )}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              </>
            )}
            {isEdit &&
              (role === 'superadmin' || role === 'admin') &&
              (client?.active_directory?.ad_password_salt_enabled ||
                !!values?.ad_password_salt?.salt?.length) && (
                <>
                  <SwitchComponent
                    id="toggle-activate-salt"
                    label={i18n.t('SALT.SALT_TOGGLE_KEY')}
                    onChange={(e, value) => {
                      setFieldValue('ad_password_salt.skip', value);
                      saltToggle = value;
                    }}
                    isChecked={values?.ad_password_salt?.skip}
                    explanationHover={
                      <ExplanationHover
                        title={`${i18n.t(
                          'SALT.SHORT_WARNING_TRUE_ACTIVATE_SALT_KEY'
                        )}`}
                      />
                    }
                  />
                  <GenericForm
                    fields={{
                      ...values,
                      salt: defaultValues?.ad_password_salt?.salt,
                    }}
                    screenFields={applyI18n(
                      screenFields.slice(7, 8),
                      i18n.language
                    )}
                    setFieldValue={setFieldValue}
                    errors={errors}
                  />
                  {saltToggle && (
                    <Grid
                      container
                      alignItems="center"
                      className={classes.saltGrid}
                    >
                      {defaultValues?.ad_password_salt?.skip === true ? (
                        <GenericForm
                          fields={{
                            ...values,
                            skip_reason:
                              defaultValues?.ad_password_salt?.skip_reason,
                          }}
                          screenFields={applyI18n(
                            screenFields.slice(9, 10),
                            i18n.language
                          )}
                          setFieldValue={setFieldValue}
                          errors={saltToggle && errors}
                        />
                      ) : (
                        <GenericForm
                          fields={{
                            ...values,
                            skip_reason:
                              defaultValues?.ad_password_salt?.skip_reason,
                          }}
                          screenFields={applyI18n(
                            screenFields.slice(8, 9),
                            i18n.language
                          )}
                          setFieldValue={setFieldValue}
                          errors={saltToggle && errors}
                        />
                      )}
                    </Grid>
                  )}
                </>
              )}
            <Grid container item xs={8} className={classes.radioGroupFields}>
              <GenericForm
                fields={{
                  ...values,
                  profile_path: client?.active_directory?.profile_path,
                }}
                screenFields={applyI18n(
                  screenFields.slice(5, 6),
                  i18n.language
                )}
                setFieldValue={setFieldValue}
                errors={errors}
              />
              {!isClientOperator(role) && (
                <GenericForm
                  fields={{
                    ...values,
                    profile_path: client?.active_directory?.profile_path,
                  }}
                  screenFields={applyI18n(
                    screenFields.slice(6, 7),
                    i18n.language
                  )}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              )}
            </Grid>

            {isEdit &&
              (defaultValues?.user_agent_info.browser_family !== null ||
                defaultValues?.created_by_operator?.email !== null ||
                defaultValues?.updated_by_operator?.email !== null) && (
                <AuditSession values={defaultValues} />
              )}

            <Grid container justify="flex-end" className={classes.buttonsBase}>
              <Grid container className={classes.buttonsAlign}>
                <Button
                  id="submit-button"
                  onClick={submitForm}
                  disabled={false}
                  submitButton
                >
                  {i18n.t(!defaultValues ? 'REGISTER_KEY' : 'SAVE_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={onCancel}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>
    </Grid>
  );
};

export default FormUser;
